<template>
  <BaseDialog
    v-model="show"
    title="Export settings"
    subtitle="Configure image export options"
    persistent
    :show-footer="true"
    :confirm-label="'Download all (' + selectedContent.length + ')'"
    confirm-button-class="base-dialog__primary-btn download-btn"
    :cancel-label="'Close'"
    :disabled="!hasSelectedPreset"
    @confirm="handleFormat"
    @cancel="handleCancel"
    content-no-padding
  >
    <div class="export-dialog-wrapper">
      <div class="export-dialog-content">
        <!-- Image options section (shown when images are selected) -->
        <div v-if="hasImages" class="export-section">
          <!-- Expandable image options section -->
          <q-expansion-item expand-icon-class="expand-icon" expand-icon="expand_more" default-opened header-class="option-section-header">
            <template v-slot:header>
              <div class="expansion-header">
                <div class="header-title-group">
                  <q-icon name="o_image" size="18px" class="section-icon" />
                  <div class="section-title">Image options</div>
                  <q-icon name="info" size="18px" color="grey-6" class="info-icon">
                    <q-tooltip>Configure how your images will be exported</q-tooltip>
                  </q-icon>
                </div>
              </div>
            </template>

            <div class="option-section-content">
              <!-- Resolution presets expansion -->
              <q-expansion-item header-class="option-group-header" expand-icon-class="expand-icon" expand-icon="expand_more" default-opened>
                <template v-slot:header>
                  <div class="expansion-header">
                    <div class="expansion-title-group">
                      <q-icon name="o_tune" size="16px" class="mini-icon" />
                      <div class="option-group-title">Resolution presets</div>
                    </div>
                  </div>
                </template>

                <div class="preset-description">Choose output quality and file size</div>

                <!-- Resolution preset cards -->
                <div class="preset-list">
                  <div v-for="preset in formatPresets" :key="preset.name" class="preset-card" :class="{ 'preset-card--active': isCurrentPreset(preset) }" @click="handlePresetSelect(preset)">
                    <div class="preset-card__content">
                      <div class="preset-card__title">{{ preset.name }}</div>
                      <div class="preset-card__description">{{ preset.description }}</div>
                    </div>
                    <div class="preset-card__size">{{ preset.letters }}</div>
                  </div>
                </div>
              </q-expansion-item>

              <!-- Watermark options -->
              <q-expansion-item v-model="watermarkExpanded" header-class="option-group-header" expand-icon-class="expand-icon" expand-icon="expand_more">
                <template v-slot:header>
                  <div class="expansion-header">
                    <div class="expansion-title-group">
                      <q-icon name="o_branding_watermark" size="16px" class="mini-icon" />
                      <div class="option-group-title">Watermark</div>
                    </div>
                  </div>
                </template>

                <!-- Watermark content when expanded -->
                <div class="watermark-section">
                  <!-- Error message alert -->
                  <q-banner v-if="watermarkError" class="bg-negative text-white q-mb-md" rounded>
                    <div class="row items-center">
                      <q-icon name="error" size="24px" class="q-mr-sm" />
                      {{ watermarkError }}
                    </div>
                    <template v-slot:action>
                      <q-btn flat color="white" label="Dismiss" @click="watermarkError = null" />
                    </template>
                  </q-banner>

                  <div class="watermark-upload-container">
                    <div class="upload-area" :class="{ 'upload-area--disabled': watermarkLoading }">
                      <template v-if="watermarkLoading">
                        <q-spinner color="primary" size="24px" />
                        <div class="upload-text">Uploading...</div>
                      </template>
                      <template v-else>
                        <q-icon name="o_cloud_upload" size="22px" color="grey-6" />
                        <div class="upload-text">Upload watermark</div>
                        <input type="file" accept=".jpg,.jpeg,.png" class="hidden-input" @change="handleWatermarkUpload($event.target.files)" :disabled="watermarkLoading" />
                      </template>
                    </div>

                    <div class="preview-container">
                      <template v-if="watermarkImage">
                        <div class="preview-with-actions">
                          <img :src="watermarkImage" alt="Watermark Preview" class="watermark-preview-img" />
                          <q-btn round flat size="sm" icon="delete" color="secondary" class="remove-watermark-btn" @click="removeWatermark" tooltip="Remove watermark" />
                        </div>
                      </template>
                      <div v-else class="preview-placeholder">
                        <q-icon name="o_image" size="22px" color="grey-6" />
                      </div>
                    </div>
                  </div>

                  <!-- Sliders -->
                  <div class="slider-group">
                    <div class="slider-row">
                      <div class="slider-label">Opacity</div>
                      <div class="slider-range">0%</div>
                      <q-slider v-model="watermarkOpacity" :min="0" :max="100" class="export-slider" color="primary" />
                      <div class="slider-range">100%</div>
                      <div class="slider-value">{{ watermarkOpacity }}%</div>
                    </div>

                    <div class="slider-row">
                      <div class="slider-label">Size (% of image width)</div>
                      <div class="slider-range">0%</div>
                      <q-slider v-model="watermarkSize" :min="5" :max="50" class="export-slider" color="primary" />
                      <div class="slider-range">100%</div>
                      <div class="slider-value">{{ watermarkSize }}%</div>
                    </div>

                    <!-- Position select -->
                    <div class="position-row">
                      <div class="position-label">Position</div>
                      <q-select v-model="watermarkPosition" :options="watermarkPositions" outlined dense emit-value map-options class="position-select" bg-color="white" />
                    </div>
                  </div>
                </div>
              </q-expansion-item>
            </div>
          </q-expansion-item>
        </div>

        <!-- Video options section (shown when videos are selected) -->
        <div v-if="hasVideos" class="export-section">
          <q-expansion-item expand-icon-class="expand-icon" expand-icon="expand_more" default-opened header-class="option-section-header">
            <template v-slot:header>
              <div class="expansion-header">
                <div class="header-title-group">
                  <q-icon name="o_videocam" size="18px" class="section-icon" />
                  <div class="section-title">Video options</div>
                  <q-icon name="info" size="18px" color="grey-6" class="info-icon">
                    <q-tooltip>Video export options</q-tooltip>
                  </q-icon>
                </div>
              </div>
            </template>

            <div class="option-section-content">
              <q-expansion-item header-class="option-group-header" expand-icon-class="expand-icon" expand-icon="expand_more" default-opened>
                <template v-slot:header>
                  <div class="expansion-header">
                    <div class="expansion-title-group">
                      <q-icon name="o_movie" size="16px" class="mini-icon" />
                      <div class="option-group-title">Video export</div>
                    </div>
                  </div>
                </template>

                <div class="video-export-info">
                  <p>Currently, videos will be exported in their original format. Advanced video options and batch tagging features coming in a future update.</p>
                </div>
              </q-expansion-item>
            </div>
          </q-expansion-item>
        </div>
      </div>
    </div>
  </BaseDialog>
</template>

<script>
import BaseDialog from './base/BaseDialog.vue';

export default {
  name: 'ImageFormatDialog',
  components: {
    BaseDialog
  },

  props: {
    value: {
      type: Boolean,
      required: true
    },
    selectedContent: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      selectedPreset: null,
      watermarkExpanded: false,
      watermarkEnabled: false,
      watermarkImage: null,
      watermarkOpacity: 60,
      watermarkSize: 20,
      watermarkPosition: 'center',
      watermarkLoading: false,
      watermarkError: null,
      watermarkPositions: [
        { value: 'top-left', label: 'Top Left' },
        { value: 'top-right', label: 'Top Right' },
        { value: 'bottom-left', label: 'Bottom Left' },
        { value: 'bottom-right', label: 'Bottom Right' },
        { value: 'center', label: 'Center' }
      ],
      formatPresets: [
        {
          name: 'HD (1280×720)',
          description: 'Perfect for social posts',
          letters: '~2MB',
          settings: {
            width: 1280,
            height: 720,
            fit: 'fit-in',
            format: 'jpeg',
            quality: 85,
            preserveAspectRatio: true
          }
        },
        {
          name: 'Full HD (1920×1080)',
          description: 'Standard quality for websites and online use',
          letters: '~5MB',
          settings: {
            width: 1920,
            height: 1080,
            fit: 'fit-in',
            format: 'jpeg',
            quality: 85,
            preserveAspectRatio: true
          }
        },
        {
          name: 'QHD (2560×1440)',
          description: 'High quality for professional printing',
          letters: '~8MB',
          settings: {
            width: 2560,
            height: 1440,
            fit: 'fit-in',
            format: 'jpeg',
            quality: 85,
            preserveAspectRatio: true
          }
        },
        {
          name: 'Original Size',
          description: 'Maximum quality for long-term storage',
          letters: '~15MB',
          settings: {
            fit: 'fit-in',
            format: 'jpeg',
            quality: 100,
            preserveAspectRatio: true
          }
        }
      ]
    };
  },

  computed: {
    show: {
      get() {
        return this.value;
      },
      set(value) {
        // Select default preset when dialog opens
        if (value && !this.selectedPreset && this.formatPresets.length > 0) {
          // Default to Full HD preset (index 1) or the first preset if not available
          this.selectedPreset = this.formatPresets[1] || this.formatPresets[0];
        }
        this.$emit('input', value);
      }
    },
    hasImages() {
      return this.selectedContent.some(item => item.type !== 'video');
    },
    hasVideos() {
      return this.selectedContent.some(item => item.type === 'video');
    },
    hasSelectedPreset() {
      return this.formatPresets.some(preset => this.isCurrentPreset(preset));
    }
  },

  methods: {
    handlePresetSelect(preset) {
      this.selectedPreset = preset;
    },

    isCurrentPreset(preset) {
      return this.selectedPreset === preset;
    },

    handleWatermarkUpload(files) {
      const file = files[0];
      if (!file) return;

      // Reset previous errors
      this.watermarkError = null;

      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        this.watermarkError = 'Please upload a valid image file (JPG, PNG, or GIF)';
        return;
      }

      // Validate file size (max 2MB)
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (file.size > maxSize) {
        this.watermarkError = 'Image is too large. Maximum size is 2MB';
        return;
      }

      // Show loading state
      this.watermarkLoading = true;

      const reader = new FileReader();
      reader.onload = e => {
        this.watermarkImage = e.target.result;
        this.watermarkEnabled = true;
        this.watermarkLoading = false;
        this.watermarkExpanded = true; // Auto-expand watermark section
      };

      reader.onerror = () => {
        this.watermarkError = 'Failed to read the image file';
        this.watermarkLoading = false;
      };

      reader.readAsDataURL(file);
    },

    removeWatermark() {
      this.watermarkImage = null;
      this.watermarkEnabled = false;
      this.watermarkError = null;
    },

    handleFormat() {
      if (this.selectedPreset) {
        const settings = {
          ...this.selectedPreset.settings
        };

        // Add watermark settings if enabled
        if (this.watermarkEnabled && this.watermarkImage) {
          // Map position to Thumbor coordinates
          let x = '0',
            y = '0';
          switch (this.watermarkPosition) {
            case 'top-left':
              x = '10p';
              y = '10p';
              break;
            case 'top-right':
              x = '-10p';
              y = '10p';
              break;
            case 'bottom-left':
              x = '10p';
              y = '-10p';
              break;
            case 'bottom-right':
              x = '-10p';
              y = '-10p';
              break;
            case 'center':
              x = 'center';
              y = 'center';
              break;
          }

          // Construct watermark settings for Thumbor
          settings.watermark = {
            image: this.watermarkImage, // Send the full base64 string
            x: x,
            y: y,
            alpha: 100 - this.watermarkOpacity, // Convert opacity to alpha (inverse)
            w_ratio: this.watermarkSize.toString(), // Width as percentage
            h_ratio: 'none' // Keep aspect ratio
          };
        }

        this.show = false;
        this.$emit('format', settings);
      }
    },

    handleCancel() {
      this.show = false;
      this.$emit('cancel');
      // Reset selections when canceling
      this.selectedPreset = null;
    }
  }
};
</script>

<style lang="scss" scoped>
.export-dialog-wrapper {
  width: 100%;
  max-height: 65vh;
  overflow-y: auto;
  position: relative;

  &::-webkit-scrollbar {
    width: 4px;
    position: absolute;
    right: 0;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
    margin: 2px;
  }
}

.export-dialog-content {
  padding: 4px;
  width: calc(100% - 6px);
}

.export-section {
  margin-bottom: 16px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.1);
  width: 100%;

  &:last-child {
    margin-bottom: 0;
  }

  .q-expansion-item__content {
    padding: 0;
  }
}

.option-section-header {
  background-color: #f8f8f8;
  padding: 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);

  .q-item__section--main {
    padding: 10px 0;
  }

  .expansion-header {
    width: 100%;
    padding: 8px 16px;
  }
}

.option-section-content {
  padding: 0;
}

.option-group {
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
}

.option-group-header {
  padding: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.03);

  .q-item__section--main {
    padding: 10px 0;
  }

  .expansion-header {
    width: 100%;
    padding: 8px 16px;
  }
}

.expansion-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.expansion-title-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toggle-header {
  display: flex;
  justify-content: space-between;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 6px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.03);
  }
}

.option-title-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-title-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-icon {
  color: var(--main-text-color);
}

.section-title {
  @include inter-font($size: 15px, $bolded: true, $color: var(--main-text-color));
}

.info-icon {
  opacity: 0.6;
  cursor: help;
}

.expand-icon {
  color: var(--secondary-text-color);
}

.mini-icon {
  color: var(--secondary-text-color);
  opacity: 0.7;
}

.option-group-title {
  @include inter-font($size: 14px, $bolded: true, $color: var(--main-text-color));
}

.preset-description {
  @include inter-font($size: 13px, $color: var(--secondary-text-color));
  margin-bottom: 12px;
  padding: 0 16px;
}

.preset-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 16px 16px;
}

.preset-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--primary, #ffd700);
  }

  &--active {
    border: 2px solid var(--primary, #ffd700);
    background-color: rgba(255, 215, 0, 0.05);
  }

  &__content {
    display: flex;
    flex-direction: column;
  }

  &__title {
    @include inter-font($size: 14px, $bolded: true, $color: var(--main-text-color));
    margin-bottom: 2px;
  }

  &__description {
    @include inter-font($size: 12px, $color: var(--secondary-text-color));
  }

  &__size {
    @include inter-font($size: 13px, $color: var(--tertiary-text-color));
  }
}

.watermark-section {
  padding: 16px;
}

.watermark-upload-container {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.upload-area {
  flex: 3;
  border: 1px dashed rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  height: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  background-color: #fafafa;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
  }

  &--disabled {
    opacity: 0.7;
    cursor: not-allowed;
    pointer-events: none;
  }
}

.upload-text {
  @include inter-font($size: 13px, $color: var(--secondary-text-color));
  margin-top: 8px;
}

.hidden-input {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.preview-container {
  flex: 2;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fafafa;
  overflow: hidden;
  position: relative;
}

.preview-with-actions {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.watermark-preview-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.remove-watermark-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  background-color: rgba(255, 255, 255, 0.8);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 1);
    transform: scale(1.05);
  }
}

.preview-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: var(--tertiary-text-color);
}

.slider-group {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.slider-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.slider-label {
  @include inter-font($size: 13px, $bolded: true, $color: var(--main-text-color));
  width: 170px;
  flex-shrink: 0;
}

.slider-range {
  @include inter-font($size: 12px, $color: var(--tertiary-text-color));
  width: 30px;
  text-align: center;
  flex-shrink: 0;
}

.export-slider {
  flex-grow: 1;
}

.slider-value {
  @include inter-font($size: 13px, $color: var(--secondary-text-color));
  width: 40px;
  text-align: right;
  flex-shrink: 0;
}

.position-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.position-label {
  @include inter-font($size: 13px, $bolded: true, $color: var(--main-text-color));
  width: 170px;
}

.position-select {
  flex-grow: 1;
}

.video-export-info {
  padding: 16px;

  p {
    @include inter-font($size: 14px, $color: var(--secondary-text-color));
    line-height: 1.5;
    margin: 0;
  }
}

.download-btn {
  background-color: #ffd700 !important;
  @include inter-font($size: 14px, $bolded: true, $color: var(--main-text-color));
}
</style>
